// src/pages/services/VLSIDesign.jsx - Premium VLSI Design Solutions
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Cpu, Zap, Activity, Settings, ArrowRight, Award, ArrowLeft, FileSearch, Target, Users, Shield, ExternalLink} from 'lucide-react';
import RelatedServices from '../../components/RelatedServices';

export default function VLSIDesign() {
  const [openFAQ, setOpenFAQ] = useState(null);

  const services = [
    {
      icon: <Cpu className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "ASIC Design",
      description: "Complete ASIC design flow from RTL to GDSII",
      features: ["RTL to GDSII", "Synthesis & Optimization", "Static Timing Analysis", "Power Analysis & Optimization"]
    },
    {
      icon: <Zap className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "FPGA Development",
      description: "FPGA prototyping and high-speed optimization",
      features: ["Rapid Prototyping", "HLS Implementation", "Timing Optimization", "Hardware Validation"]
    },
    {
      icon: <Activity className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "RTL Design & Verification",
      description: "High-quality RTL with SystemVerilog and UVM",
      features: ["SystemVerilog Design", "UVM Framework", "Coverage Analysis", "Assertion-Based Verification"]
    },
    {
      icon: <Settings className="w-6 h-6 sm:w-8 sm:h-8" />,
      title: "Physical Design",
      description: "Floor planning, placement, routing for advanced nodes",
      features: ["Floorplanning", "Place & Route", "Clock Tree Synthesis", "Timing & Power Closure"]
    }
  ];

  const projectTypes = [
    { type: "Communication ICs", icon: "📡", examples: ["Ethernet PHY", "SerDes", "Wireless RF"] },
    { type: "Processor Cores", icon: "🖥️", examples: ["ARM Cores", "RISC-V", "Custom CPUs"] },
    { type: "Memory Controllers", icon: "💾", examples: ["DDR4/DDR5", "LPDDR", "Flash Controllers"] },
    { type: "Signal Processing", icon: "📊", examples: ["DSP Cores", "Image Processing", "Audio Codecs"] },
    { type: "Power Management", icon: "🔋", examples: ["PMIC", "Voltage Regulators", "Battery Management"] },
    { type: "IoT & Edge AI", icon: "📱", examples: ["Sensor ICs", "BLE Chips", "Edge AI Accelerators"] }
  ];

  const stats = [
    { value: "50+", label: "Tape-outs Completed" },
    { value: "100M+", label: "Gates Designed" },
    { value: "15+", label: "Technology Nodes" },
    { value: "99%", label: "First-Pass Success" }
  ];

  const achievements = [
    "Successfully taped out 50+ ASIC designs",
    "Experience with latest 7nm & 5nm nodes",
    "Industry-leading best practices & methodologies",
    "Expertise in automotive & aerospace standards",
    "Partnerships with leading foundries",
    "24/7 support for mission-critical projects"
  ];

  const designFlow = [
    { step: '1', title: 'Specification', desc: 'Requirements analysis', icon: '📋' },
    { step: '2', title: 'Architecture', desc: 'System design', icon: '🏗️' },
    { step: '3', title: 'RTL Design', desc: 'HDL coding', icon: '💻' },
    { step: '4', title: 'Verification', desc: 'UVM testing', icon: '✅' },
    { step: '5', title: 'Synthesis', desc: 'Gate optimization', icon: '⚙️' },
    { step: '6', title: 'Physical', desc: 'Place & route', icon: '🗺️' },
    { step: '7', title: 'Validation', desc: 'DRC/LVS sign-off', icon: '🔍' },
    { step: '8', title: 'Tape-out', desc: 'GDS delivery', icon: '🎯' }
  ];

  const pricingPlans = [
    {
      name: 'RTL Design',
      price: 999999,
      monthlySupport: 9999,
      color: 'purple',
      badge: null,
      features: [
        'RTL Coding in SystemVerilog',
        'Basic Functional Verification',
        'Synthesis & Optimization',
        'Timing Analysis',
        'Documentation & IP Package',
        '3 Months Support Included'
      ]
    },
    {
      name: 'Complete ASIC',
      price: 2999999,
      monthlySupport: 29999,
      color: 'indigo',
      badge: 'MOST POPULAR',
      featured: true,
      features: [
        'Full RTL to GDSII Flow',
        'Comprehensive UVM Verification',
        'Synthesis & STA',
        'Physical Design & Placement',
        'Power & Thermal Analysis',
        '6 Months Support Included',
        'Tape-out Guidance',
        'Sign-off Ready GDS'
      ]
    },
    {
      name: 'Enterprise SoC',
      price: null,
      monthlySupport: 99999,
      color: 'purple',
      badge: null,
      features: [
        'Complex Multi-core SoC Design',
        'Dedicated Design Team',
        'Advanced Node Support (7nm/5nm)',
        'Full Verification & Validation',
        '24/7 Priority Support',
        'Continuous Process Integration',
        'Foundry Partnership Support'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-12 sm:py-20 bg-gradient-to-br from-purple-600 via-violet-600 to-indigo-700 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/services" className="inline-flex items-center gap-2 text-white hover:underline mb-4 sm:mb-6 text-xs sm:text-base font-medium">
            <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" /> Back to Services
          </Link>
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black mb-4 sm:mb-6 leading-tight">
                VLSI Design Services
              </h1>
              <p className="text-base sm:text-lg lg:text-xl text-purple-100 mb-6 sm:mb-8 leading-relaxed">
                Expert semiconductor design from RTL to silicon. Cutting-edge VLSI solutions with proven 50+ tape-out success rate.
              </p>
              <div className="flex flex-col gap-3 sm:gap-4">
                <Link to="/contact" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-center text-sm sm:text-base shadow-lg">
                  Discuss Your Project
                </Link>
                <Link to="/portfolio" className="px-4 sm:px-8 py-2.5 sm:py-4 bg-transparent border-2 border-white rounded-lg font-semibold hover:bg-white/10 transition-colors text-center text-sm sm:text-base">
                  View VLSI Portfolio
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=600&h=400&fit=crop" alt="VLSI Design" className="rounded-2xl shadow-2xl w-full" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 sm:py-16 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center bg-white dark:bg-gray-900 p-3 sm:p-6 rounded-xl shadow-lg hover:shadow-xl transition border border-gray-200/50 dark:border-gray-700/50 group">
                <div className="text-2xl sm:text-4xl font-black text-purple-600 mb-1 sm:mb-2 group-hover:scale-110 transition-transform">{stat.value}</div>
                <div className="text-xs sm:text-lg font-bold text-gray-900 dark:text-white mb-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Our VLSI Design Services
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto px-2">
              End-to-end VLSI design solutions for all semiconductor needs
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center text-purple-600 mb-3 sm:mb-4">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-xs sm:text-base text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">{service.description}</p>
                <ul className="space-y-1.5 sm:space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 dark:text-gray-400">
                      <Check className="w-3 h-3 sm:w-4 sm:h-4 text-green-500 flex-shrink-0" /> {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VLSI Capabilities Section - Focused on Expertise */}
      <section className="py-12 sm:py-20 lg:py-24 bg-gradient-to-br from-purple-600/10 via-violet-600/10 to-indigo-700/10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl lg:max-w-7xl mx-auto">
          <div className="bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl p-6 sm:p-8 lg:p-12 shadow-2xl border border-purple-200/50 dark:border-purple-800/50">
            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8 lg:gap-12">
              {/* Left Content - Expertise & Approach */}
              <div className="lg:w-1/2">
                <div className="text-center lg:text-left">
                  <Cpu className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 text-purple-600 mx-auto lg:mx-0 mb-4 lg:mb-6 opacity-75 animate-pulse" />
                  <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-700 bg-clip-text text-transparent mb-4 lg:mb-6">
                    Expert VLSI Design Services
                  </h2>
                  <p className="text-base sm:text-lg lg:text-xl text-gray-700 dark:text-gray-300 mb-6 lg:mb-8 leading-relaxed">
                    We provide comprehensive VLSI design solutions using industry-standard methodologies. 
                    Whether you need RTL design, verification, or complete ASIC development, 
                    <strong className="font-semibold text-purple-600 dark:text-purple-400 block lg:inline"> we deliver quality semiconductor designs</strong>. 
                    Share your requirements, and we'll build the solution you need.
                  </p>
                </div>

                {/* Design Expertise - Desktop */}
                <div className="hidden lg:grid grid-cols-2 gap-6 mb-8">
                  <div className="group p-6 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Cpu className="w-12 h-12 text-purple-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">ASIC & FPGA Design</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">Custom Chip Design</div>
                          <div className="text-lg font-bold text-violet-600">RTL to GDSII</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="group p-6 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <div className="flex items-center gap-4">
                      <Activity className="w-12 h-12 text-purple-600 group-hover:scale-110 transition-transform" />
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Verification & Validation</h3>
                        <div className="space-y-1">
                          <div className="text-lg font-medium text-gray-600 dark:text-gray-400">SystemVerilog & UVM</div>
                          <div className="text-lg font-bold text-violet-600">Comprehensive Testing</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop CTA Buttons */}
                <div className="hidden lg:flex flex-col gap-4">
                  <Link to="/contact" className="px-8 py-4 bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-700 text-white rounded-2xl font-black text-lg shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    💻 Start VLSI Project
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link to="/portfolio" className="px-8 py-4 border-2 border-purple-600 text-purple-600 dark:text-purple-400 rounded-2xl font-black text-lg hover:bg-purple-600 hover:text-white transition-all duration-300 text-center tracking-wide flex items-center justify-center gap-3">
                    View Our Work
                    <ExternalLink className="w-5 h-5" />
                  </Link>
                </div>
              </div>

              {/* Right Content - VLSI Capabilities */}
              <div className="lg:w-1/2">
                <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <Zap className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-purple-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">Digital Design</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-purple-600">Logic Design</div>
                      <div className="text-base sm:text-lg font-medium text-violet-600">Synthesis & Optimization</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2">
                    <Shield className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 text-purple-600 mx-auto mb-3 sm:mb-4 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2 lg:mb-3 text-center">Physical Design</h3>
                    <div className="space-y-1 sm:space-y-2 text-center">
                      <div className="text-lg sm:text-xl lg:text-xl font-bold text-purple-600">Place & Route</div>
                      <div className="text-base sm:text-lg font-medium text-violet-600">Timing Closure</div>
                    </div>
                  </div>
                  
                  <div className="group p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 rounded-2xl border border-purple-200/50 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 lg:col-span-2">
                    <div className="flex flex-col sm:flex-row items-center gap-4 lg:gap-6">
                      <div className="text-4xl">🛠️</div>
                      <div className="text-center sm:text-left">
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2">Full VLSI Flow</h3>
                        <div className="text-xl sm:text-2xl lg:text-2xl font-bold text-purple-600">RTL Design to Silicon</div>
                        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">Complete semiconductor design services</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Design Process - Mobile */}
                <div className="mt-6 lg:hidden">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="group p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 rounded-xl border border-purple-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Specification</div>
                        <div className="text-sm text-purple-600">Requirements & Architecture</div>
                      </div>
                    </div>
                    <div className="group p-4 bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/30 dark:to-violet-900/30 rounded-xl border border-purple-200/50">
                      <div className="text-center">
                        <div className="text-lg font-bold text-gray-900 dark:text-white">Implementation</div>
                        <div className="text-sm text-purple-600">RTL & Physical Design</div>
                      </div>
                    </div>
                  </div>

                  {/* Mobile/Tablet CTA Buttons */}
                  <div className="flex flex-col gap-3 sm:gap-4">
                    <Link to="/contact" className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-700 text-white rounded-2xl font-black text-sm sm:text-base shadow-2xl hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300 text-center tracking-wide">
                      💻 Start VLSI Project
                    </Link>
                    <Link to="/portfolio" className="px-6 sm:px-8 py-3 sm:py-4 border-2 sm:border-4 border-purple-600 text-purple-600 dark:text-purple-400 rounded-2xl font-black text-sm sm:text-base hover:bg-purple-600 hover:text-white transition-all duration-300 text-center tracking-wide">
                      View Our Work
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* VLSI Design Capabilities - Desktop Only */}
            <div className="hidden lg:grid grid-cols-4 gap-4 mt-12 pt-8 border-t border-purple-200/50 dark:border-purple-800/50">
              {[
                { label: 'RTL Design', value: 'SystemVerilog', icon: '💻', description: 'Verilog/VHDL' },
                { label: 'Verification', value: 'UVM Framework', icon: '✅', description: 'Testbenches' },
                { label: 'Synthesis', value: 'Logic Optimization', icon: '⚙️', description: 'Gate-level' },
                { label: 'Physical Design', value: 'Floorplan & Route', icon: '🗺️', description: 'Placement' }
              ].map((capability, index) => (
                <div key={index} className="text-center p-4 bg-gradient-to-br from-purple-50/50 to-violet-50/50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-xl hover:shadow-lg transition-all duration-300">
                  <div className="text-3xl mb-2">{capability.icon}</div>
                  <div className="text-2xl font-black text-purple-600">{capability.value}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">{capability.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">{capability.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* Project Types */}
      <section className="py-12 sm:py-20 bg-gray-50 dark:bg-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-4">
              Project Types We Handle
            </h2>
            <p className="text-sm sm:text-lg text-gray-600 dark:text-gray-400 px-2">
              Experience across diverse VLSI applications and industries
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
            {projectTypes.map((project, index) => (
              <div key={index} className="bg-white dark:bg-gray-900 rounded-xl p-4 sm:p-6 shadow-md border border-gray-200 dark:border-gray-700 text-center hover:shadow-xl transition-all group hover:-translate-y-2">
                <div className="text-4xl sm:text-5xl mb-3 sm:mb-4 group-hover:scale-110 transition-transform">{project.icon}</div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">{project.type}</h3>
                <div className="space-y-1.5 sm:space-y-2">
                  {project.examples.map((example, idx) => (
                    <div key={idx} className="bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 px-2 sm:px-3 py-1 sm:py-1.5 rounded text-xs sm:text-sm font-medium">
                      {example}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Design Flow */}
      <section className="py-12 sm:py-20 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-8 sm:mb-12 text-gray-900 dark:text-white">
            VLSI Design Flow
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 sm:gap-4">
            {designFlow.map((phase, index) => (
              <div key={index} className="text-center group">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-600 to-indigo-600 text-white rounded-full flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-2 sm:mb-3 shadow-lg group-hover:scale-110 transition-transform">
                  {phase.icon}
                </div>
                <div className="text-xs sm:text-sm font-bold text-purple-600 mb-0.5 sm:mb-1">{phase.step}</div>
                <h3 className="text-xs sm:text-base font-bold text-gray-900 dark:text-white mb-0.5 sm:mb-1">{phase.title}</h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">{phase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-700 text-white px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-12 items-center">
            <div>
              <Target className="w-10 h-10 sm:w-12 sm:h-12 mb-4 sm:mb-6" />
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
                Why Choose Our VLSI Services?
              </h2>
              <ul className="space-y-3 sm:space-y-4">
                {achievements.map((achievement, index) => (
                  <li key={index} className="flex items-start gap-2 sm:gap-3">
                    <Check className="w-4 h-4 sm:w-6 sm:h-6 flex-shrink-0 mt-0.5 sm:mt-1" />
                    <span className="text-xs sm:text-base">{achievement}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/20">
              <FileSearch className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-6" />
              <h3 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4">Get Expert Consultation</h3>
              <p className="text-purple-100 mb-4 sm:mb-6 text-sm sm:text-base">
                Discuss your VLSI project. From architecture to tape-out support, we're here to help.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-4 sm:px-8 py-2 sm:py-4 bg-white text-purple-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-xs sm:text-base">
                <span>Schedule Consultation</span>
                <ArrowRight className="w-3 h-3 sm:w-5 sm:h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-white via-gray-50 to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-800 px-2 sm:px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 sm:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-gray-900 dark:text-white mb-3 sm:mb-4">
              VLSI Design Pricing
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto px-2 leading-relaxed">
              Flexible pricing for VLSI projects. From RTL design to complete ASIC tape-out.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto mb-12 sm:mb-20">
            {pricingPlans.map((plan, idx) => {
              const colorMap = {
                purple: { bg: 'from-purple-600 to-violet-600', light: 'from-purple-50 to-violet-50', hover: 'hover:border-purple-200' },
                indigo: { bg: 'from-violet-600 to-indigo-700', light: 'from-violet-50 to-indigo-50', hover: 'hover:border-indigo-200' }
              };
              const colors = colorMap[plan.color];

              return (
                <div
                  key={idx}
                  className={`group relative ${plan.featured ? 'md:scale-105' : ''} bg-white dark:bg-gray-900/80 backdrop-blur-sm p-6 sm:p-8 rounded-2xl shadow-lg dark:shadow-2xl border border-gray-200/60 dark:border-gray-700/60 hover:shadow-xl transition-all duration-300 flex flex-col ${colors.hover} dark:hover:border-gray-600/50 ${plan.featured ? 'border-2 border-indigo-500 dark:border-indigo-600' : ''}`}
                >
                  {/* Glow effect for featured */}
                  {plan.featured && (
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 dark:from-indigo-600/20 dark:to-purple-600/20 rounded-2xl opacity-100 -z-10 blur-xl"></div>
                  )}

                  {/* Badge */}
                  {plan.badge && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <div className={`bg-gradient-to-r ${colors.bg} text-white px-4 sm:px-6 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-black shadow-lg`}>
                        {plan.badge} ⭐
                      </div>
                    </div>
                  )}

                  <h3 className={`text-2xl sm:text-3xl font-black text-gray-900 dark:text-white mb-2 ${plan.featured ? 'mt-4 sm:mt-6' : ''}`}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 font-medium">
                    {plan.name === 'RTL Design' && 'RTL to synthesis flow'}
                    {plan.name === 'Complete ASIC' && 'End-to-end ASIC delivery'}
                    {plan.name === 'Enterprise SoC' && 'Complex multi-core systems'}
                  </p>

                  {/* Price */}
                  <div className="mb-8">
                    {plan.price ? (
                      <>
                        <div className="flex items-baseline gap-1 mb-2">
                          <span className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #7c3aed' : '#7c3aed, #4f46e5'})`}}>
                            ${Math.round(plan.price / 83).toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">one-time</span>
                        </div>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #7c3aed' : '#7c3aed, #4f46e5'})`}}>
                            ${Math.round(plan.monthlySupport / 83).toLocaleString()}
                          </span>
                          <span className="text-gray-600 dark:text-gray-400 font-semibold">/month</span>
                        </div>
                      </>
                    ) : (
                      <div>
                        <div className="text-4xl sm:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: `linear-gradient(to right, ${plan.color === 'purple' ? '#9333ea, #7c3aed' : '#7c3aed, #4f46e5'})`}}>
                          ${Math.round(plan.monthlySupport / 83).toLocaleString()}
                        </div>
                        <span className="text-gray-600 dark:text-gray-400 font-semibold">/month retainer</span>
                      </div>
                    )}
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 font-medium">Custom scoping available</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10 flex-grow">
                    {plan.features.map((feature, featureIdx) => (
                      <li key={featureIdx} className="flex items-start gap-3">
                        <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5`} style={{backgroundColor: `${plan.color === 'purple' ? '#f3e8ff' : '#e0e7ff'}`}}>
                          <Check className="w-3 h-3 sm:w-4 sm:h-4 font-bold" style={{color: `${plan.color === 'purple' ? '#9333ea' : '#4f46e5'}`}} />
                        </div>
                        <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <Link
                    to="/contact"
                    className={`w-full px-6 py-3 sm:py-4 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all text-sm sm:text-base transform hover:-translate-y-1 text-center ${
                      plan.featured
                        ? `bg-gradient-to-r ${colors.bg} text-white hover:shadow-2xl`
                        : 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                    }`}
                  >
                    {plan.name === 'Enterprise SoC' ? 'Let\'s Talk' : 'Get Started'}
                  </Link>
                </div>
              );
            })}
          </div>

          {/* All Plans Include */}
          <div className="max-w-5xl mx-auto mb-12 sm:mb-16">
            <h3 className="text-center text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-8">All Plans Include</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
              {[
                { icon: '🏗️', text: 'Architecture Design' },
                { icon: '✅', text: 'Verification Plan' },
                { icon: '📊', text: 'Power Analysis' },
                { icon: '📚', text: 'Full Documentation' }
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 bg-white dark:bg-gray-800/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                  <span className="text-2xl">{item.icon}</span>
                  <span className="text-sm sm:text-base text-gray-700 dark:text-gray-300 font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 rounded-2xl border border-purple-200/50 dark:border-purple-800/50 p-6 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">VLSI Design FAQ</h3>
            <div className="space-y-4 sm:space-y-5">
              {[
                {
                  q: 'What is your tape-out success rate?',
                  a: 'We have a 99%+ first-pass success rate with 50+ completed tape-outs. Our rigorous verification methodology and design expertise ensure silicon-ready quality on first submission.'
                },
                {
                  q: 'Which technology nodes do you support?',
                  a: 'We have experience across 15+ technology nodes from 180nm down to 5nm and below. We work with major foundries (TSMC, Samsung, GlobalFoundries) and maintain cutting-edge design tools for advanced nodes.'
                },
                {
                  q: 'How long does an ASIC design take?',
                  a: 'RTL design: 2-3 months. Complete ASIC (RTL to GDSII): 6-9 months. Timelines depend on complexity, specifications, and verification requirements. We provide detailed schedules after technical review.'
                },
                {
                  q: 'Do you handle post-silicon validation?',
                  a: 'Yes! We provide post-silicon debug support, bring-up assistance, and firmware integration. We also offer long-term maintenance packages for production support and optimization.'
                },
                {
                  q: 'What design tools do you use?',
                  a: 'We use industry-standard tools: Synopsys (Design Compiler, ICC, VCS), Cadence (Virtuoso, Conformal), Mentor Graphics, and more. We\'re also experienced with latest open-source EDA tools.'
                }
              ].map((faq, idx) => (
                <details key={idx} className="group cursor-pointer">
                  <summary className="flex items-center justify-between font-semibold text-gray-900 dark:text-white py-3 px-4 bg-white dark:bg-gray-800/50 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <span className="text-sm sm:text-base text-left pr-4">{faq.q}</span>
                    <span className="group-open:rotate-180 transition-transform flex-shrink-0">▼</span>
                  </summary>
                  <p className="text-sm text-gray-600 dark:text-gray-300 p-4 bg-white/50 dark:bg-gray-800/30">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 bg-gradient-to-r from-purple-600 via-violet-600 to-indigo-700 text-white px-2 sm:px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Cpu className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 animate-bounce" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 sm:mb-6">
            Ready to Design Your Next Chip?
          </h2>
          <p className="text-base sm:text-xl text-purple-100 mb-6 sm:mb-8 px-2">
            Let's bring your silicon vision to life. From RTL to tape-out, we deliver excellence.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 sm:px-10 py-3 sm:py-5 bg-white text-purple-600 rounded-2xl font-black text-base sm:text-xl shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all duration-300"
          >
            <span>💻 Schedule VLSI Consultation</span>
            <ArrowRight className="w-4 h-4 sm:w-6 sm:h-6" />
          </Link>
        </div>
      </section>
      <div className="mt-16">
      <RelatedServices currentService="vlsi-design" />
      </div>
    </div>
  );
}
